import {useEffect} from 'react';

// Adapted from Hosanna use-songs-fullscreen-wake-lock.ts; pending requests are serialized.
export default function useScreenWakeLock(active) {
  useEffect(() => {
    if (!active || typeof navigator.wakeLock?.request !== 'function') return;
    let sentinel = null, pending = false, disposed = false, generation = 0;
    const safelyRelease = lock => Promise.resolve().then(() => lock?.release()).catch(() => {});
    const onReleased = () => { sentinel?.removeEventListener?.('release', onReleased); sentinel = null; };
    const release = () => {
      generation += 1;
      const lock = sentinel;
      sentinel = null;
      lock?.removeEventListener?.('release', onReleased);
      if (lock) void safelyRelease(lock);
    };
    const acquire = async () => {
      if (disposed || document.visibilityState !== 'visible' || sentinel || pending) return;
      pending = true;
      const requestedGeneration = generation;
      try {
        const lock = await navigator.wakeLock.request('screen');
        if (disposed || document.visibilityState !== 'visible' || requestedGeneration !== generation) {
          await safelyRelease(lock);
        } else {
          sentinel = lock;
          lock.addEventListener?.('release', onReleased);
        }
      } catch {
        // Unsupported policies, battery saving or OS denial leave the song view usable.
      } finally {
        pending = false;
        // Visibility may have changed twice while the browser was answering the request.
        if (!disposed && requestedGeneration !== generation && document.visibilityState === 'visible') void acquire();
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void acquire();
      else release();
    };
    void acquire();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      release();
    };
  }, [active]);
}
